import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { storageKeys } from '@/config/env';
import { useAuthStore } from '@/features/auth/auth.store';
import { api } from '@/lib/api/client';
import { isLanguage, type Language } from './index';

/** `preferredLanguage` on the user record is a numeric enum. */
const LANGUAGE_CODE: Record<Language, number> = { en: 0, es: 1 };

/**
 * Changing the language, in one place.
 *
 * Every route to the choice — the switcher on the auth screens, the item in the
 * user menu — has to write the same key and push the same preference to the
 * server, or the choice survives on one path and not the other. In the old app
 * the header switcher and the dropdown items each did their own thing, and a
 * separate effect in App.js re-posted `users/me` on every render that touched
 * the profile.
 */
export function useLanguage() {
  const { i18n } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  const language: Language = isLanguage(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : 'en';

  const setLanguage = useCallback(
    async (next: Language) => {
      if (next === language) return;

      await i18n.changeLanguage(next);
      localStorage.setItem(storageKeys.language, next);
      document.documentElement.lang = next;

      // Persisting the preference is a convenience, not part of the switch —
      // a failure here must not undo a language the user can already see.
      if (isAuthenticated) {
        try {
          await api.post('users/me', { preferredLanguage: LANGUAGE_CODE[next] });
        } catch {
          /* ignored on purpose */
        }
      }
    },
    [i18n, language, isAuthenticated]
  );

  return { language, setLanguage };
}
